import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, MessageCircle, Share2, MoreHorizontal, Image, Video, 
  Smile, Send, X, TrendingUp, Hash, Search, Bookmark, 
  Repeat2, ChevronDown, Sparkles, Flame, Clock, Users,
  Globe, Lock, Camera, Mic, MapPin, Calendar, Radio, Star,
  Play, Eye, Zap, Crown, Award, Verified
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import Logo, { LogoIcon } from './Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Post Composer Component
export const PostComposer = ({ onPostCreated, compact = false }) => {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = async () => {
    if (!content.trim() && mediaUrls.length === 0) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/posts`, {
        content: content.trim(),
        media_urls: mediaUrls,
        media_type: mediaUrls.length > 0 ? 'image' : null
      });
      
      setContent('');
      setMediaUrls([]);
      setIsExpanded(false);
      onPostCreated?.(response.data);
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setMediaUrls(prev => [...prev, response.data.url]);
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      alert('Failed to upload media');
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = (index) => {
    setMediaUrls(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className={`bg-[#08080C] rounded-2xl border border-white/5 ${compact ? 'p-3' : 'p-4'}`}>
      <div className="flex gap-3">
        <img
          src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=0F0F16&color=00F0FF`}
          alt={user?.name}
          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
        />
        <div className="flex-1">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onFocus={() => setIsExpanded(true)}
            placeholder="What's on your mind?"
            className="w-full bg-transparent text-white placeholder-gray-500 outline-none resize-none text-base"
            rows={isExpanded ? 3 : 1}
            data-testid="post-composer-input"
          />
          
          {/* Media Preview */}
          {mediaUrls.length > 0 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {mediaUrls.map((url, index) => (
                <div key={index} className="relative group">
                  <img
                    src={url}
                    alt="Upload preview"
                    className="w-20 h-20 object-cover rounded-xl"
                  />
                  <button
                    onClick={() => removeMedia(index)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-[#FF2A2A] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Actions */}
          <AnimatePresence>
            {(isExpanded || content || mediaUrls.length > 0) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center justify-between mt-3 pt-3 border-t border-white/5"
              >
                <div className="flex gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="p-2 rounded-xl hover:bg-white/5 text-[#00F0FF] transition-colors touch-target"
                    data-testid="add-media-button"
                  >
                    <Image className="w-5 h-5" />
                  </button>
                  <button className="p-2 rounded-xl hover:bg-white/5 text-[#FFE800] transition-colors touch-target">
                    <Smile className="w-5 h-5" />
                  </button>
                  <button className="p-2 rounded-xl hover:bg-white/5 text-[#00FF66] transition-colors touch-target">
                    <MapPin className="w-5 h-5" />
                  </button>
                </div>
                
                <motion.button
                  onClick={handleSubmit}
                  disabled={loading || (!content.trim() && mediaUrls.length === 0)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-5 py-2 rounded-full font-semibold text-black bg-gradient-to-r from-[#00F0FF] to-[#00FF66] disabled:opacity-50 flex items-center gap-2 touch-target"
                  data-testid="post-submit-button"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Post
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
};

// Post Card Component
export const PostCard = ({ post, onLike, onComment, onRepost, onDelete }) => {
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [loadingComment, setLoadingComment] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isLiked, setIsLiked] = useState(post.liked_by_me);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);

  const handleLike = async () => {
    try {
      const response = await axios.post(`${API}/posts/${post.id}/like`);
      setIsLiked(response.data.liked);
      setLikeCount(response.data.like_count);
      onLike?.(post.id, response.data.liked);
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    
    setLoadingComment(true);
    try {
      const response = await axios.post(`${API}/posts/${post.id}/comment`, {
        content: commentText.trim()
      });
      setComments(prev => [response.data, ...prev]);
      setCommentText('');
      onComment?.(post.id);
    } catch (error) {
      console.error('Error commenting:', error);
    } finally {
      setLoadingComment(false);
    }
  };

  const handleRepost = async () => {
    try {
      await axios.post(`${API}/posts/${post.id}/repost`);
      onRepost?.(post.id);
    } catch (error) {
      console.error('Error reposting:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/posts/${post.id}`);
      onDelete?.(post.id);
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const loadComments = async () => {
    try {
      const response = await axios.get(`${API}/posts/${post.id}`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  useEffect(() => {
    if (showComments && comments.length === 0) {
      loadComments();
    }
  }, [showComments]);

  // If this is a repost, show the original post
  const displayPost = post.is_repost && post.original_post ? post.original_post : post;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#08080C] rounded-2xl border border-white/5 overflow-hidden"
      data-testid={`post-card-${post.id}`}
    >
      {/* Repost Header */}
      {post.is_repost && (
        <div className="px-4 pt-3 flex items-center gap-2 text-gray-500 text-sm">
          <Repeat2 className="w-4 h-4" />
          <span>{post.user_name} reposted</span>
        </div>
      )}

      {/* Post Header */}
      <div className="p-4 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <img
            src={displayPost.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayPost.user_name)}&background=0F0F16&color=00F0FF`}
            alt={displayPost.user_name}
            className="w-11 h-11 rounded-full object-cover"
          />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-white">{displayPost.user_name}</span>
              {displayPost.is_verified && (
                <span className="w-4 h-4 bg-[#00F0FF] rounded-full flex items-center justify-center text-[10px] text-black font-bold">✓</span>
              )}
            </div>
            <span className="text-sm text-gray-500">
              {formatDistanceToNow(new Date(displayPost.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 rounded-full hover:bg-white/5 text-gray-400"
          >
            <MoreHorizontal className="w-5 h-5" />
          </button>
          
          {showMenu && (
            <div className="absolute right-0 top-10 bg-[#0F0F16] rounded-xl border border-white/10 py-2 min-w-[150px] z-10">
              <button className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-white/5 flex items-center gap-2">
                <Bookmark className="w-4 h-4" /> Save Post
              </button>
              {post.user_id === user?.id && (
                <button
                  onClick={handleDelete}
                  className="w-full px-4 py-2 text-left text-sm text-[#FF2A2A] hover:bg-white/5 flex items-center gap-2"
                >
                  <X className="w-4 h-4" /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Post Content */}
      <div className="px-4 pb-3">
        <p className="text-white whitespace-pre-wrap break-words">
          {displayPost.content?.split(/(#\w+|@\w+)/g).map((part, i) => {
            if (part.startsWith('#')) {
              return <span key={i} className="text-[#00F0FF] hover:underline cursor-pointer">{part}</span>;
            }
            if (part.startsWith('@')) {
              return <span key={i} className="text-[#FFE800] hover:underline cursor-pointer">{part}</span>;
            }
            return part;
          })}
        </p>
      </div>

      {/* Media */}
      {displayPost.media_urls && displayPost.media_urls.length > 0 && (
        <div className={`grid gap-1 ${displayPost.media_urls.length > 1 ? 'grid-cols-2' : ''}`}>
          {displayPost.media_urls.map((url, index) => (
            <img
              key={index}
              src={url}
              alt="Post media"
              className="w-full object-cover max-h-[400px]"
            />
          ))}
        </div>
      )}

      {/* Engagement Stats */}
      <div className="px-4 py-2 flex items-center gap-4 text-sm text-gray-500 border-t border-white/5">
        <span>{likeCount} likes</span>
        <span>{post.comment_count || 0} comments</span>
        <span>{post.repost_count || 0} reposts</span>
        <span className="ml-auto">{post.view_count || 0} views</span>
      </div>

      {/* Actions */}
      <div className="px-4 py-2 flex items-center justify-around border-t border-white/5">
        <button
          onClick={handleLike}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors touch-target ${
            isLiked ? 'text-[#FF2A2A]' : 'text-gray-400 hover:text-[#FF2A2A] hover:bg-[#FF2A2A]/10'
          }`}
          data-testid={`like-button-${post.id}`}
        >
          <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
          <span className="text-sm font-medium hidden sm:inline">Like</span>
        </button>
        
        <button
          onClick={() => setShowComments(!showComments)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-gray-400 hover:text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors touch-target"
          data-testid={`comment-button-${post.id}`}
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm font-medium hidden sm:inline">Comment</span>
        </button>
        
        <button
          onClick={handleRepost}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-gray-400 hover:text-[#00FF66] hover:bg-[#00FF66]/10 transition-colors touch-target"
          data-testid={`repost-button-${post.id}`}
        >
          <Repeat2 className="w-5 h-5" />
          <span className="text-sm font-medium hidden sm:inline">Repost</span>
        </button>
        
        <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-gray-400 hover:text-[#FFE800] hover:bg-[#FFE800]/10 transition-colors touch-target">
          <Share2 className="w-5 h-5" />
          <span className="text-sm font-medium hidden sm:inline">Share</span>
        </button>
      </div>

      {/* Comments Section */}
      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-white/5"
          >
            {/* Comment Input */}
            <div className="p-4 flex gap-3">
              <img
                src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=0F0F16&color=00F0FF`}
                alt="You"
                className="w-8 h-8 rounded-full"
              />
              <div className="flex-1 flex gap-2">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                  placeholder="Write a comment..."
                  className="flex-1 px-4 py-2 rounded-full bg-[#0F0F16] border border-white/10 text-white text-sm placeholder-gray-500 outline-none focus:border-[#00F0FF]"
                  data-testid={`comment-input-${post.id}`}
                />
                <button
                  onClick={handleComment}
                  disabled={loadingComment || !commentText.trim()}
                  className="p-2 rounded-full bg-[#00F0FF] text-black disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Comments List */}
            <div className="px-4 pb-4 space-y-3 max-h-[300px] overflow-y-auto">
              {comments.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <img
                    src={comment.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.user_name)}&background=0F0F16&color=00F0FF`}
                    alt={comment.user_name}
                    className="w-8 h-8 rounded-full"
                  />
                  <div className="flex-1">
                    <div className="bg-[#0F0F16] rounded-2xl px-4 py-2">
                      <span className="font-semibold text-sm text-white">{comment.user_name}</span>
                      <p className="text-sm text-gray-300">{comment.content}</p>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      <span>{formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}</span>
                      <button className="hover:text-[#FF2A2A]">Like</button>
                      <button className="hover:text-[#00F0FF]">Reply</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Trending Sidebar Component
export const TrendingSidebar = () => {
  const [trendingHashtags, setTrendingHashtags] = useState([]);
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrending();
  }, []);

  const fetchTrending = async () => {
    try {
      const [hashtagsRes, postsRes] = await Promise.all([
        axios.get(`${API}/hashtags/trending`),
        axios.get(`${API}/posts/trending?limit=5`)
      ]);
      setTrendingHashtags(hashtagsRes.data);
      setTrendingPosts(postsRes.data);
    } catch (error) {
      console.error('Error fetching trending:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Trending Hashtags */}
      <div className="bg-[#08080C] rounded-2xl border border-white/5 p-4">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-[#FF2A2A]" />
          <h3 className="font-bold text-white">Trending</h3>
        </div>
        
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-[#0F0F16] rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {trendingHashtags.slice(0, 5).map((hashtag, index) => (
              <a
                key={hashtag.tag}
                href={`#/hashtag/${hashtag.tag}`}
                className="block p-3 rounded-xl hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[#00F0FF]">#{hashtag.tag}</p>
                    <p className="text-xs text-gray-500">{hashtag.post_count} posts</p>
                  </div>
                  <span className="text-2xl font-bold text-gray-600">#{index + 1}</span>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Who to Follow */}
      <div className="bg-[#08080C] rounded-2xl border border-white/5 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-[#FFE800]" />
          <h3 className="font-bold text-white">Who to Follow</h3>
        </div>
        
        <div className="space-y-3">
          {[
            { name: 'Gaming Pro', handle: '@gamingpro', avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100', verified: true },
            { name: 'Music Studio', handle: '@musicstudio', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100', verified: false },
            { name: 'Tech News', handle: '@technews', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100', verified: true }
          ].map((user) => (
            <div key={user.handle} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <div className="flex items-center gap-1">
                    <span className="font-semibold text-sm text-white">{user.name}</span>
                    {user.verified && (
                      <span className="w-4 h-4 bg-[#00F0FF] rounded-full flex items-center justify-center text-[8px] text-black font-bold">✓</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{user.handle}</span>
                </div>
              </div>
              <button className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white text-black hover:bg-gray-200 transition-colors">
                Follow
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Discover Page Component - Trending Streamers & Popular Creators
export const DiscoverPage = () => {
  const { user } = useAuth();
  const [featuredStreamers, setFeaturedStreamers] = useState([]);
  const [popularCreators, setPopularCreators] = useState([]);
  const [trendingHashtags, setTrendingHashtags] = useState([]);
  const [liveStreams, setLiveStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    fetchDiscoverData();
  }, []);

  const fetchDiscoverData = async () => {
    setLoading(true);
    try {
      const [streamersRes, hashtagsRes, streamsRes, creatorsRes] = await Promise.all([
        axios.get(`${API}/streams/featured?limit=6`).catch(() => ({ data: [] })),
        axios.get(`${API}/hashtags/trending?limit=8`),
        axios.get(`${API}/streams/live?limit=4`).catch(() => ({ data: [] })),
        axios.get(`${API}/creators/popular?limit=6`).catch(() => ({ data: { creators: [] } }))
      ]);
      
      setFeaturedStreamers(streamersRes.data || []);
      setTrendingHashtags(hashtagsRes.data || []);
      setLiveStreams(streamsRes.data || []);
      
      // Use real creators data if available, otherwise use fallback
      const realCreators = creatorsRes.data?.creators || [];
      if (realCreators.length > 0) {
        setPopularCreators(realCreators);
      } else {
        // Fallback to sample data when no real creators exist
        setPopularCreators([
          { id: '1', name: 'Alex Gaming', handle: '@alexgaming', avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150', followers: 125000, posts: 342, verified: true, category: 'Gaming' },
          { id: '2', name: 'Sarah Music', handle: '@sarahmusic', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150', followers: 89000, posts: 567, verified: true, category: 'Music' },
          { id: '3', name: 'Tech Mike', handle: '@techmike', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150', followers: 67000, posts: 234, verified: true, category: 'Technology' },
          { id: '4', name: 'Creative Luna', handle: '@creativeluna', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150', followers: 45000, posts: 189, verified: false, category: 'Art' },
          { id: '5', name: 'Fitness Pro', handle: '@fitnesspro', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150', followers: 112000, posts: 456, verified: true, category: 'Fitness' },
          { id: '6', name: 'Cook Master', handle: '@cookmaster', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150', followers: 78000, posts: 321, verified: false, category: 'Cooking' },
        ]);
      }
    } catch (error) {
      console.error('Error fetching discover data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/search/posts?q=${encodeURIComponent(query)}&limit=10`);
      setSearchResults(response.data.posts || []);
    } catch (error) {
      console.error('Error searching:', error);
    }
  };

  const formatFollowers = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const categories = [
    { name: 'Gaming', icon: '🎮', color: 'from-purple-500 to-pink-500' },
    { name: 'Music', icon: '🎵', color: 'from-pink-500 to-red-500' },
    { name: 'Art', icon: '🎨', color: 'from-yellow-500 to-orange-500' },
    { name: 'Tech', icon: '💻', color: 'from-cyan-500 to-blue-500' },
    { name: 'Sports', icon: '⚽', color: 'from-green-500 to-emerald-500' },
    { name: 'Cooking', icon: '🍳', color: 'from-orange-500 to-red-500' },
  ];

  return (
    <div className="min-h-screen bg-[#030305] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#030305]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <LogoIcon size="sm" animate={false} />
              <h1 className="text-lg font-bold text-white hidden sm:block">Discover</h1>
            </div>
            
            {/* Search Bar */}
            <div className="flex-1 max-w-md mx-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search creators, hashtags..."
                  className="w-full pl-10 pr-4 py-2 rounded-full bg-[#0F0F16] border border-white/10 text-sm text-white placeholder-gray-500 outline-none focus:border-[#00F0FF] transition-colors"
                  data-testid="discover-search"
                />
                {searchQuery && (
                  <button 
                    onClick={() => { setSearchQuery(''); setSearchResults([]); setIsSearching(false); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        {/* Search Results */}
        {isSearching && searchResults.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Search className="w-5 h-5 text-[#00F0FF]" />
              Search Results
            </h2>
            <div className="space-y-3">
              {searchResults.map((post) => (
                <div key={post.id} className="bg-[#08080C] rounded-xl p-4 border border-white/5">
                  <div className="flex items-center gap-3 mb-2">
                    <img
                      src={post.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(post.user_name)}&background=0F0F16&color=00F0FF`}
                      alt={post.user_name}
                      className="w-8 h-8 rounded-full"
                    />
                    <span className="font-medium text-white">{post.user_name}</span>
                  </div>
                  <p className="text-gray-300 text-sm line-clamp-2">{post.content}</p>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {!isSearching && (
          <>
            {/* Categories */}
            <section>
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#FFE800]" />
                Explore Categories
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {categories.map((cat) => (
                  <motion.button
                    key={cat.name}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`p-4 rounded-2xl bg-gradient-to-br ${cat.color} flex flex-col items-center gap-2 shadow-lg`}
                    data-testid={`category-${cat.name.toLowerCase()}`}
                  >
                    <span className="text-2xl">{cat.icon}</span>
                    <span className="text-xs font-semibold text-white">{cat.name}</span>
                  </motion.button>
                ))}
              </div>
            </section>

            {/* Live Now Section */}
            {liveStreams.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Radio className="w-5 h-5 text-[#FF2A2A] animate-pulse" />
                  Live Now
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {liveStreams.map((stream) => (
                    <motion.div
                      key={stream.id}
                      whileHover={{ scale: 1.02 }}
                      className="bg-[#08080C] rounded-2xl overflow-hidden border border-white/5 group cursor-pointer"
                    >
                      <div className="relative aspect-video bg-gradient-to-br from-[#0F0F16] to-[#08080C]">
                        <div className="absolute top-2 left-2 px-2 py-1 bg-[#FF2A2A] rounded-md text-xs font-bold text-white flex items-center gap-1">
                          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          LIVE
                        </div>
                        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 rounded-md text-xs text-white flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {stream.viewer_count || 0}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                          <Play className="w-12 h-12 text-white" />
                        </div>
                      </div>
                      <div className="p-3">
                        <p className="font-semibold text-white text-sm truncate">{stream.title}</p>
                        <p className="text-gray-500 text-xs">{stream.streamer_name}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* Popular Creators */}
            <section>
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Crown className="w-5 h-5 text-[#FFE800]" />
                Popular Creators
              </h2>
              {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="bg-[#08080C] rounded-2xl p-4 animate-pulse">
                      <div className="w-16 h-16 mx-auto bg-[#0F0F16] rounded-full mb-3" />
                      <div className="h-4 bg-[#0F0F16] rounded w-3/4 mx-auto mb-2" />
                      <div className="h-3 bg-[#0F0F16] rounded w-1/2 mx-auto" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {popularCreators.map((creator) => (
                    <motion.div
                      key={creator.id}
                      whileHover={{ scale: 1.03, y: -5 }}
                      className="bg-[#08080C] rounded-2xl p-4 border border-white/5 hover:border-[#00F0FF]/30 transition-all cursor-pointer text-center"
                      data-testid={`creator-${creator.id}`}
                    >
                      <div className="relative inline-block mb-3">
                        <img
                          src={creator.avatar}
                          alt={creator.name}
                          className="w-16 h-16 rounded-full object-cover mx-auto ring-2 ring-[#00F0FF]/50"
                        />
                        {creator.verified && (
                          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#00F0FF] rounded-full flex items-center justify-center">
                            <Verified className="w-4 h-4 text-black" />
                          </div>
                        )}
                      </div>
                      <h3 className="font-semibold text-white text-sm truncate">{creator.name}</h3>
                      <p className="text-gray-500 text-xs mb-2">{creator.handle}</p>
                      <div className="flex items-center justify-center gap-1 text-xs text-gray-400 mb-3">
                        <Users className="w-3 h-3" />
                        <span>{formatFollowers(creator.followers)}</span>
                      </div>
                      <span className="inline-block px-2 py-1 rounded-full bg-[#0F0F16] text-[10px] text-[#00F0FF] font-medium">
                        {creator.category}
                      </span>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="mt-3 w-full py-2 rounded-full bg-[#00F0FF] text-black text-xs font-semibold hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] transition-all"
                      >
                        Follow
                      </motion.button>
                    </motion.div>
                  ))}
                </div>
              )}
            </section>

            {/* Trending Hashtags */}
            <section>
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#00FF66]" />
                Trending Hashtags
              </h2>
              <div className="flex flex-wrap gap-3">
                {trendingHashtags.map((hashtag, index) => (
                  <motion.button
                    key={hashtag.tag}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#08080C] border border-white/5 hover:border-[#00F0FF]/30 transition-all"
                    data-testid={`hashtag-${hashtag.tag}`}
                  >
                    <span className="text-2xl font-bold text-gray-600">#{index + 1}</span>
                    <div className="text-left">
                      <p className="text-[#00F0FF] font-semibold text-sm">#{hashtag.tag}</p>
                      <p className="text-gray-500 text-xs">{hashtag.post_count || 0} posts</p>
                    </div>
                  </motion.button>
                ))}
              </div>
            </section>

            {/* Featured Streamers */}
            {featuredStreamers.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#FFE800]" />
                  Featured Streamers
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {featuredStreamers.map((streamer) => (
                    <motion.div
                      key={streamer.id}
                      whileHover={{ scale: 1.02 }}
                      className="bg-gradient-to-br from-[#08080C] to-[#0F0F16] rounded-2xl p-4 border border-white/5 flex items-center gap-4"
                    >
                      <img
                        src={streamer.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(streamer.name)}&background=0F0F16&color=00F0FF`}
                        alt={streamer.name}
                        className="w-14 h-14 rounded-full object-cover ring-2 ring-[#FFE800]/50"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white truncate">{streamer.name}</h3>
                          {streamer.is_verified && (
                            <Award className="w-4 h-4 text-[#FFE800] flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-gray-500 text-sm truncate">{streamer.category || 'Streamer'}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {formatFollowers(streamer.followers || 0)}
                          </span>
                        </div>
                      </div>
                      <button className="px-4 py-2 rounded-full bg-[#FFE800] text-black text-xs font-semibold hover:shadow-[0_0_20px_rgba(255,232,0,0.4)] transition-all">
                        Follow
                      </button>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* Suggested for You based on interests */}
            <section>
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#FF2A2A]" />
                Recommended for You
              </h2>
              <div className="bg-gradient-to-r from-[#08080C] to-[#0F0F16] rounded-2xl p-6 border border-white/5">
                <p className="text-gray-400 text-center mb-4">Follow more creators to get personalized recommendations!</p>
                <div className="flex justify-center">
                  <button className="px-6 py-3 rounded-full bg-gradient-to-r from-[#00F0FF] to-[#00FF66] text-black font-semibold hover:shadow-[0_0_30px_rgba(0,240,255,0.4)] transition-all">
                    Explore More Creators
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

// Main Social Feed Component
const SocialFeed = ({ initialTab = 'foryou' }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef();

  // Update activeTab when initialTab prop changes
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const fetchPosts = useCallback(async (pageNum = 1, append = false) => {
    if (activeTab === 'discover') return; // Skip fetching for discover tab
    try {
      const endpoint = activeTab === 'foryou' ? '/posts/explore' : '/posts/feed';
      const response = await axios.get(`${API}${endpoint}?page=${pageNum}&limit=10`);
      
      if (append) {
        setPosts(prev => [...prev, ...response.data.posts]);
      } else {
        setPosts(response.data.posts);
      }
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'discover') {
      setLoading(true);
      setPage(1);
      fetchPosts(1, false);
    }
  }, [activeTab, fetchPosts]);

  // Infinite scroll
  const lastPostRef = useCallback(node => {
    if (loading || activeTab === 'discover') return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
        fetchPosts(page + 1, true);
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [loading, hasMore, page, activeTab, fetchPosts]);

  const handlePostCreated = useCallback((newPost) => {
    setPosts(prev => [newPost, ...prev]);
  }, []);

  const handlePostDeleted = useCallback((postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
  }, []);

  // If discover tab is active, render DiscoverPage
  if (activeTab === 'discover') {
    return <DiscoverPage />;
  }

  return (
    <div className="min-h-screen bg-[#030305]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#030305]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <LogoIcon size="sm" animate={false} />
            
            {/* Tab Navigation */}
            <div className="flex gap-1 bg-[#0F0F16] rounded-full p-1">
              <button
                onClick={() => setActiveTab('foryou')}
                className={`px-3 sm:px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'foryou' 
                    ? 'bg-[#00F0FF] text-black' 
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="tab-foryou"
              >
                <Sparkles className="w-4 h-4 inline mr-1" />
                <span className="hidden sm:inline">For You</span>
              </button>
              <button
                onClick={() => setActiveTab('following')}
                className={`px-3 sm:px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'following' 
                    ? 'bg-[#00F0FF] text-black' 
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="tab-following"
              >
                <Users className="w-4 h-4 inline mr-1" />
                <span className="hidden sm:inline">Following</span>
              </button>
              <button
                onClick={() => setActiveTab('discover')}
                className={`px-3 sm:px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'discover' 
                    ? 'bg-[#FFE800] text-black' 
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="tab-discover"
              >
                <Search className="w-4 h-4 inline mr-1" />
                <span className="hidden sm:inline">Discover</span>
              </button>
            </div>

            {/* Search */}
            <div className="hidden md:flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 rounded-full bg-[#0F0F16] border border-white/10 text-sm text-white placeholder-gray-500 outline-none focus:border-[#00F0FF] w-48"
                  data-testid="search-input"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-6">
          {/* Feed Column */}
          <div className="flex-1 max-w-[600px] mx-auto lg:mx-0 space-y-4">
            {/* Post Composer */}
            <PostComposer onPostCreated={handlePostCreated} />

            {/* Posts */}
            {loading && posts.length === 0 ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-[#08080C] rounded-2xl p-4 animate-pulse">
                    <div className="flex gap-3 mb-4">
                      <div className="w-11 h-11 bg-[#0F0F16] rounded-full" />
                      <div className="flex-1">
                        <div className="h-4 bg-[#0F0F16] rounded w-1/3 mb-2" />
                        <div className="h-3 bg-[#0F0F16] rounded w-1/4" />
                      </div>
                    </div>
                    <div className="h-4 bg-[#0F0F16] rounded w-full mb-2" />
                    <div className="h-4 bg-[#0F0F16] rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                <p className="text-lg font-medium text-white mb-2">No posts yet</p>
                <p className="text-gray-500">Be the first to share something!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((post, index) => (
                  <div
                    key={post.id}
                    ref={index === posts.length - 1 ? lastPostRef : null}
                  >
                    <PostCard
                      post={post}
                      onDelete={handlePostDeleted}
                    />
                  </div>
                ))}
                
                {loading && hasMore && (
                  <div className="flex justify-center py-4">
                    <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Hidden on mobile */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-20">
              <TrendingSidebar />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialFeed;
